import { useCallback } from 'react';
import { FormControl, Select, MenuItem, Box, Typography, CircularProgress, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useTechnicians } from '../api/useTechnicians';
import { useTranslation } from 'react-i18next';

interface TechnicianSelectProps {
  value: string;
  onChange: (technicianId: string) => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'standard' | 'filled';
  disabled?: boolean;
  fullWidth?: boolean;
  showColorIndicator?: boolean;
  clearable?: boolean;
  sx?: Record<string, unknown>;
}

export const TechnicianSelect = ({
  value,
  onChange,
  placeholder,
  size = 'small',
  variant = 'standard',
  disabled = false,
  fullWidth = false,
  showColorIndicator = true,
  clearable = true,
  sx = {},
}: TechnicianSelectProps) => {
  const { t } = useTranslation();
  const { data: technicians, isLoading } = useTechnicians();

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange('');
    },
    [onChange]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <FormControl variant={variant} size={size} fullWidth={fullWidth} disabled={disabled} sx={{ position: 'relative' }}>
      <Select value={value} onChange={(e) => onChange(e.target.value)} displayEmpty sx={sx}>
        <MenuItem value="">
          <em>{placeholder ?? t('technicians.selectTechnician')}</em>
        </MenuItem>
        {(technicians ?? []).map((technician) => (
          <MenuItem key={technician.id} value={technician.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: technician.isActive ? 1 : 0.6 }}>
              {showColorIndicator && (
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: technician.color,
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography variant="inherit">
                {technician.displayName}
                {!technician.isActive && ` (${t('common.inactive')})`}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {clearable && value && (
        <IconButton
          size="small"
          onClick={handleClear}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={disabled}
          aria-label={t('common.remove')}
          sx={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', zIndex: 1, p: 0.25 }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </FormControl>
  );
};
